import type { Mutation, QueryClient } from '@tanstack/react-query';
import i18n from 'i18next';
import { cortarRotaDoLog, semParametrosDeTexto } from '@malgoos/shared';
import { ErroRequisicao } from './api';
import { sensivelPorNome } from './sensivel-por-nome';
import { ehFalhaDeChunk } from './versao';

/**
 * CRM no novo desenho, lote 0 (ficha `demandas/entrada/2026-10-26_malgo_MOD_crm-novo-desenho.md`, "Log de erros no núcleo"):
 * o erro que a tela pega vai ao log do núcleo (`POST /erros`) com a ação que a pessoa tentava (o rótulo do último botão
 * clicado), a tela (a janela aberta), o que estava preenchido (sem senha, arquivo nem documento), o erro, a pilha, o
 * navegador e o `request_id` da falha 5xx. Entram: mutação que falhou sem ser recusa do domínio (4xx), consulta com 5xx,
 * erro de render e o erro solto da janela. Ficam de fora: queda de rede, recuo (429/503) e pedaço do app que sumiu no deploy.
 * O envio usa `fetch` direto: não passa pelo 401 da sessão nem se registra a si mesmo.
 */
export type OrigemErro = 'acao' | 'render' | 'api' | 'janela' | 'simulado';

interface Clique {
  rotulo: string;
  em: number;
  alvo: Element;
}
let ultimoClique: Clique | null = null;
/** Por erro registrado nesta aba, como refazer a ação (a mutação com as mesmas variáveis). Some ao recarregar. */
const refazer = new Map<string, () => Promise<unknown>>();
const ouvintes = new Set<(id: string) => void>();
let qcAtual: QueryClient | null = null;
/** Módulo da tela pela rota (o `main.tsx` passa o que lê os módulos web); o servidor confere se está instalado. */
let moduloDaRotaAtual: ((caminho: string) => string | null) | null = null;

const texto = (v: string | null | undefined) => (v ?? '').replace(/\s+/g, ' ').trim();

/**
 * Rótulo do controle clicado (correções de 02/11, rcorr-1): não se adivinha o que é nome. Só vai o que é comprovadamente texto da
 * interface, ou seja, igual a um texto fixo dos dicionários carregados (sem `{{...}}`), ou o `data-acao-log` que o próprio
 * controle declara. Todo o resto vira o genérico do tipo: "Maria da Silva", "Sobre o contato: Maria" e "Tornar Padaria da Ana a
 * empresa principal" não são texto fixo, "Excluir" é. O genérico é gravado em pt e a tela de leitura o traduz (`LogsErros`).
 */
const GENERICO_POR_TIPO: { seletor: string; texto: string }[] = [
  { seletor: '[role="option"]', texto: 'Opção' },
  { seletor: 'a', texto: 'Link' },
  { seletor: 'summary', texto: 'Seção' },
  { seletor: 'label', texto: 'Campo' },
];
const GENERICO_BOTAO = 'Botão';
const TODOS_OS_CONTROLES = `${GENERICO_POR_TIPO.map((g) => g.seletor).join(',')},button,[role="button"],[role="menuitem"],[role="tab"],input[type="submit"],input[type="button"]`;

let textosFixos: { idioma: string; textos: Set<string> } | null = null;

/** Os textos fixos do idioma ativo: montados uma vez por idioma (os dicionários não mudam depois do início), sem custo por clique. */
function textosFixosDoIdioma(): Set<string> {
  const idioma = (i18n.resolvedLanguage ?? i18n.language ?? 'pt').slice(0, 2);
  if (textosFixos?.idioma === idioma) return textosFixos.textos;
  const textos = new Set<string>();
  const coletar = (v: unknown) => {
    if (typeof v === 'string') {
      if (!v.includes('{{')) textos.add(texto(v));
    } else if (v && typeof v === 'object') Object.values(v).forEach(coletar);
  };
  const recursos = i18n.store?.data[idioma];
  coletar(recursos);
  if (recursos) textosFixos = { idioma, textos };
  return textos;
}

/** O texto é igual a um texto fixo da interface, no idioma ativo? */
export const ehTextoFixo = (v: string) => textosFixosDoIdioma().has(texto(v));

export function rotuloDoControle(el: Element | null): string {
  const c = el?.closest(TODOS_OS_CONTROLES);
  if (!c) return '';
  const declarado = texto(c.getAttribute('data-acao-log'));
  if (declarado) return declarado.slice(0, 120);
  const id = c.getAttribute('aria-labelledby');
  const candidatos = [c.getAttribute('aria-label'), id ? document.getElementById(id)?.textContent : '', c.textContent, c.getAttribute('title'), (c as HTMLInputElement).value];
  const fixo = candidatos.find((v) => v && ehTextoFixo(v));
  if (fixo) return texto(fixo).slice(0, 120);
  return GENERICO_POR_TIPO.find((g) => c.matches(g.seletor))?.texto ?? GENERICO_BOTAO;
}

/** Janela ou painel aberto por cima da página: o último diálogo. */
function dialogoAberto(): Element | null {
  const ds = [...document.querySelectorAll('[role="dialog"],[role="alertdialog"],dialog[open]')];
  return ds.at(-1) ?? null;
}

/** Chave da tela pela rota: dois primeiros trechos; trecho com número ou id vira `:id` (nunca o nome do registro). */
function chaveDaRota(caminho: string): string {
  const trechos = caminho.split('/').filter(Boolean).slice(0, 2).map((t) => (/\d/.test(t) ? ':id' : t));
  return `/${trechos.join('/')}`;
}

/**
 * Lista de 02/11 (R7-SEG-M1): o rótulo da tela é o item de menu ativo (ou a chave da rota), nunca o `h1` do registro nem o título
 * de diálogo, que pode trazer o nome do contato ou da empresa. Com diálogo aberto o rótulo é genérico ("<tela> (janela)"); o
 * botão clicado (`acao`) diz qual. O feedback usa `comJanela: false`, porque o diálogo aberto é o dele mesmo.
 */
export function nomeDaTela(comJanela = true): string {
  const item = document.querySelector('a[aria-current="page"][data-pagina-casca]') ?? document.querySelector('a[aria-current="page"][data-item-menu]');
  const nome = texto(item?.querySelector('span')?.textContent || item?.textContent).slice(0, 80) || chaveDaRota(location.pathname);
  return comJanela && dialogoAberto() ? `${nome} (janela)` : nome;
}

/** A rota atual como vai para o log: sem parâmetro de texto (`?busca=`, `?voltar=`, `?lx_q=`) e sem escape cortado. */
export const rotaAtualDoLog = () => cortarRotaDoLog(location.pathname + semParametrosDeTexto(location.search));

/**
 * O que estava preenchido na janela aberta (ou no formulário do último clique): sem senha, arquivo, oculto nem nada que a
 * regra única `sensivelPorNome` classifique (documento, conta, agência, Pix, dinheiro, e-mail, telefone, endereço,
 * nascimento, nome de pessoa; rodada crm1, SEG-M1). O servidor tem a lista própria de chaves proibidas.
 */
/** Id gerado pelo React: sem o nome do campo, não dá para saber se é sensível. */
const CHAVE_OPACA = /^(_r_|:r|«r)/i;
/** O nome do registro (contato, empresa) é dado pessoal e não ajuda a diagnosticar. */
const NOME_DE_REGISTRO = /(^|[._])nome$/i;
/** Lista de 02/11 (R8-SEG-B1): o título da negociação é texto livre e costuma trazer o nome do cliente; fora do log. */
const TITULO_DE_REGISTRO = /titulo|title/i;

export function dadosPreenchidos(raiz: ParentNode | null): Record<string, string | boolean> {
  const saida: Record<string, string | boolean> = {};
  if (!raiz) return saida;
  for (const el of raiz.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input,select,textarea')) {
    // rl0211-5 (SEG-M1): o id dos campos do `Formulario` vem do `useId` (`_R_1_`) e não diz o que o campo é, então os filtros por
    // nome não valiam. A chave é o nome real (`data-campo`); campo sem nome conhecido não vai.
    const campo = el.closest<HTMLElement>('[data-campo]')?.dataset.campo;
    const chave = campo || el.name || el.id;
    // rl0211-6: campo de busca mostra o nome escolhido (empresa, cliente, responsável), e campo personalizado tem regra própria de
    // sensível que o nome da chave não revela. Nenhum dos dois ajuda a diagnosticar.
    if (!chave || CHAVE_OPACA.test(chave) || NOME_DE_REGISTRO.test(chave) || TITULO_DE_REGISTRO.test(chave) || chave.startsWith('extras.') || el.getAttribute('role') === 'combobox' || Object.keys(saida).length >= 60) continue;
    const tipo = (el as HTMLInputElement).type;
    if (tipo === 'password' || tipo === 'file' || tipo === 'hidden' || el.closest('[data-sensivel]')) continue;
    if (sensivelPorNome(chave)) continue;
    saida[chave.slice(0, 80)] = tipo === 'checkbox' || tipo === 'radio' ? (el as HTMLInputElement).checked : String(el.value).slice(0, 300);
  }
  return saida;
}

function raizDosDados(): ParentNode | null {
  return dialogoAberto() ?? (ultimoClique && Date.now() - ultimoClique.em < 10_000 ? ultimoClique.alvo.closest('form') : null) ?? null;
}

/** Erro que vale registrar: falha do sistema, não recusa de regra, queda de rede nem deploy novo. */
export function valeRegistrar(e: unknown, origem: OrigemErro): boolean {
  if (origem === 'simulado') return true;
  if (!e || ehFalhaDeChunk(e)) return false;
  if (e instanceof ErroRequisicao) return e.status >= 500 && e.status !== 503;
  if (e instanceof TypeError && /fetch|network|load failed/i.test(e.message)) return false;
  // Validação local da tela (`throw new Error(t('...'))` na mutação) é recusa já traduzida para a pessoa, não falha.
  if (origem === 'acao' && e instanceof Error && e.constructor === Error) return false;
  if (e instanceof Error && (e.name === 'ErroEmRecuo' || e.name === 'AbortError' || /ResizeObserver loop/.test(e.message))) return false;
  return true;
}

const requestIdDo = (e: unknown) => {
  if (!(e instanceof ErroRequisicao)) return undefined;
  const d = e.corpo.details as { request_id?: unknown } | undefined;
  return d && typeof d === 'object' && typeof d.request_id === 'string' ? d.request_id : undefined;
};

const mensagemDo = (e: unknown) => {
  if (e instanceof ErroRequisicao) return `${e.status} ${e.corpo.code}${e.corpo.message ? `: ${e.corpo.message}` : ''}`;
  if (e instanceof Error) return `${e.name !== 'Error' ? `${e.name}: ` : ''}${e.message || 'Erro sem mensagem'}`;
  return String(e ?? 'Erro desconhecido');
};

/** Grava no log. Devolve o id do registro (null: não registrou, por regra, teto ou falha do próprio envio). */
export async function registrarErro(
  e: unknown,
  opcoes: {
    origem: OrigemErro;
    acao?: string;
    refazer?: () => Promise<unknown>;
  },
): Promise<string | null> {
  if (!valeRegistrar(e, opcoes.origem)) return null;
  const recente = ultimoClique && Date.now() - ultimoClique.em < 10_000 ? ultimoClique.rotulo : '';
  const acao =
    opcoes.acao ||
    (opcoes.origem === 'simulado'
      ? 'Simular erro (teste)'
      : opcoes.origem === 'render'
        ? `Abrir a tela${recente ? ` (${recente})` : ''}`
        : recente || (opcoes.origem === 'api' ? 'Carregar a tela' : 'Fora de um clique'));
  const corpo = {
    origem: opcoes.origem,
    acao: acao.slice(0, 120),
    rota: rotaAtualDoLog(),
    tela: nomeDaTela() || undefined,
    dados: dadosPreenchidos(raizDosDados()),
    erro: mensagemDo(e).slice(0, 1000),
    pilha: e instanceof Error && e.stack ? e.stack.split('\n').slice(0, 12).join('\n').slice(0, 4000) : undefined,
    navegador: navigator.userAgent.replace(/^Mozilla\/5\.0 /, '').slice(0, 200),
    request_id: requestIdDo(e),
    modulo: moduloDaRotaAtual?.(location.pathname) ?? undefined,
  };
  try {
    const r = await fetch('/api/v1/erros', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(corpo),
    });
    if (!r.ok) return null;
    const { id } = (await r.json()) as { id: string | null };
    if (!id) return null;
    if (opcoes.refazer) refazer.set(id, opcoes.refazer);
    ouvintes.forEach((f) => f(id));
    return id;
  } catch {
    return null;
  }
}

/** A ação deste erro pode ser refeita nesta aba (a mesma mutação, com as mesmas variáveis)? */
export const podeRefazer = (id: string) => refazer.has(id);
export function refazerErro(id: string): Promise<unknown> | null {
  const f = refazer.get(id);
  return f ? f() : null;
}

/** Avisa quem mostra o "registramos o erro" (o toast, montado dentro do provedor). */
export function assinarErrosRegistrados(f: (id: string) => void): () => void {
  ouvintes.add(f);
  return () => void ouvintes.delete(f);
}

/** Refazer a mutação: uma mutação nova com as mesmas opções e variáveis (o `onSuccess` da tela roda de novo). */
function refazerMutacao(m: Mutation<unknown, unknown, unknown, unknown>): (() => Promise<unknown>) | undefined {
  const qc = qcAtual;
  if (!qc || m.state.variables === undefined) return undefined;
  const opcoes = m.options;
  const variaveis = m.state.variables;
  return () => qc.getMutationCache().build(qc, opcoes).execute(variaveis);
}

/**
 * Liga a captura: os cliques (para saber a ação), as falhas das mutações e das consultas do QueryClient e os erros soltos
 * da janela. Uma vez, no `main.tsx`.
 */
export function instalarLogDeErros(qc: QueryClient, moduloDaRota?: (caminho: string) => string | null) {
  qcAtual = qc;
  moduloDaRotaAtual = moduloDaRota ?? null;
  document.addEventListener(
    'click',
    (ev) => {
      const alvo = ev.target instanceof Element ? ev.target : null;
      const rotulo = rotuloDoControle(alvo);
      if (alvo && rotulo) ultimoClique = { rotulo, em: Date.now(), alvo };
    },
    true,
  );
  qc.getMutationCache().subscribe((ev) => {
    if (ev.type !== 'updated' || ev.action.type !== 'error') return;
    void registrarErro(ev.action.error, {
      origem: 'acao',
      refazer: refazerMutacao(ev.mutation as Mutation<unknown, unknown, unknown, unknown>),
    });
  });
  qc.getQueryCache().subscribe((ev) => {
    if (ev.type !== 'updated' || ev.action.type !== 'error') return;
    // O React Query só marca o erro depois das novas tentativas do 5xx: um registro por consulta que falhou de vez.
    void registrarErro(ev.action.error, { origem: 'api' });
  });
  window.addEventListener('error', (ev) => {
    if (ev.error) void registrarErro(ev.error, { origem: 'janela' });
  });
  window.addEventListener('unhandledrejection', (ev) => void registrarErro(ev.reason, { origem: 'janela' }));
}
